import { useState } from "react";
import { motion } from "framer-motion";
import { Zap, ArrowDownRight, FileDown, ShieldCheck, PlayCircle } from "lucide-react";
import { scrollToHash } from "./Header";

const HERO_IMG = "https://images.unsplash.com/photo-1769794371055-54436b54577e?q=80&w=1400&auto=format&fit=crop";

const FLOAT_CARDS = [
  { icon: FileDown, title: "Instant PDF", sub: "Download on payment", cls: "left-[-8%] top-[8%]", anim: "floaty" },
  { icon: ShieldCheck, title: "Razorpay Secure", sub: "UPI · Cards · NetBanking", cls: "right-[-6%] top-[42%]", anim: "floaty-slow" },
  { icon: PlayCircle, title: "Video Courses", sub: "Learn anywhere", cls: "left-[2%] bottom-[-6%]", anim: "floaty" },
];

export default function Hero({ settings = {} }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const eyebrow = settings.hero_eyebrow || "PDF STORE · VIDEO COURSES · INSTANT DOWNLOAD";
  const line1 = settings.hero_line1 || "Topper Notes &";
  const line2 = settings.hero_line2 || "Video Courses";
  const line3 = settings.hero_line3 || "— delivered instantly.";
  const sub = settings.hero_sub || "Buy handwritten PDF notes, question banks and full video courses. Pay securely with Razorpay — UPI, cards or netbanking — and download instantly.";

  const LINES = [
    [{ t: line1 }],
    [{ t: line2, em: true }],
    [{ t: line3 }],
  ];

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
  };

  return (
    <section id="top" onMouseMove={onMove} className="relative min-h-[96vh] flex items-center overflow-hidden bg-ink text-white grain pt-28 pb-16">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute -top-40 right-[-10%] w-[42rem] h-[42rem] rounded-full bg-amber-500/15 blur-[140px]" />
      <div className="absolute bottom-[-30%] left-[-10%] w-[36rem] h-[36rem] rounded-full bg-emerald-500/10 blur-[140px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.22em] text-amber-400"
            data-testid="hero-eyebrow"
          >
            <Zap size={13} className="text-amber-400" /> {eyebrow}
          </motion.div>

          <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={`${i}-${line[0].t}`} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.map((seg, j) => (
                    <span key={j} className={seg.em ? "italic text-amber-400" : ""}>{seg.t}</span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            {sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToHash("#catalog")}
              data-testid="hero-cta-browse"
              className="group inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-display font-bold text-ink hover:bg-amber-400 transition-all hover:gap-3"
            >
              Browse the Store <ArrowDownRight size={18} className="transition-transform group-hover:rotate-45" />
            </button>
            <button
              onClick={() => scrollToHash("#youtube")}
              data-testid="hero-cta-youtube"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 font-display font-semibold text-white backdrop-blur hover:border-amber-400/60 hover:text-amber-300 transition-colors"
            >
              <PlayCircle size={18} /> Watch Free Lessons
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
          >
            <span>Razorpay Secured</span><span className="text-amber-500/70">/</span>
            <span>Instant Delivery</span><span className="text-amber-500/70">/</span>
            <span>Owner-Curated Library</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="tilt-soft relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-black/40"
            style={{ transform: `perspective(900px) rotateY(${tilt.x * 5}deg) rotateX(${-tilt.y * 5}deg)` }}
            data-testid="hero-visual"
          >
            <img src={HERO_IMG} alt="Study desk with notes" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/20" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(245,158,11,0.25),transparent_55%)]" />
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/10 bg-ink/60 backdrop-blur-xl px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400">Featured Bundle</p>
              <p className="font-display font-bold text-lg mt-1">Full Syllabus Masterclass</p>
              <p className="text-sm text-slate-400 mt-0.5">Video + Notes + Study Group</p>
            </div>
          </motion.div>

          {FLOAT_CARDS.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 + i * 0.15, duration: 0.6 }}
              className={`tilt-soft absolute ${c.cls} ${c.anim} hidden sm:flex items-center gap-3 rounded-2xl border border-white/10 bg-ink/70 backdrop-blur-xl px-4 py-3 shadow-xl shadow-black/30`}
              style={{ transform: `translate(${tilt.x * (12 + i * 6)}px, ${tilt.y * (12 + i * 6)}px)` }}
              data-testid={`hero-float-card-${i}`}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400"><c.icon size={18} /></span>
              <span>
                <span className="block font-display text-sm font-bold leading-tight">{c.title}</span>
                <span className="block text-xs text-slate-400">{c.sub}</span>
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
